/* UGC detail modal - repopulates one shared template per carousel section

   The gesture is resolved from pointer events, not a click listener, because
   the carousel's onPointerDown calls viewport.setPointerCapture(), which
   retargets the browser's synthesized click to the viewport - so
   closest('[data-ugc-open]') on a click handler finds nothing. pointerdown's
   own target is still trustworthy: capture only affects later events. The photo
   also cannot be an <a>/<button>, since onPointerDown bails on those and the
   photo would stop being draggable. Keyboard has its own Enter/Space path. */

(function () {
  'use strict';

  var CARD = '.ugc-carousel-card';
  var TRIGGER = '[data-ugc-open]';
  var ROOT = '[data-ugc-carousel]';
  var REAL_CARD = '.ugc-carousel-card:not([data-ugc-clone])';

  /* same 10px as the drag-fix and the carousel's own INTENT_THRESHOLD */
  var THRESHOLD = 10;
  /* longer than this is a long-press, not a tap */
  var TAP_MAX_MS = 700;
  /* must match .yg-ugc-modal's transition duration in yg-custom.css */
  var CLOSE_ANIMATION_MS = 200;
  /* How long after opening a backdrop click is ignored - see openedAt. */
  var OPEN_CLICK_GRACE_MS = 300;

  /** pointerId -> { x, y, t, trigger } */
  var gestures = new Map();

  /** The modal currently on screen, or null. */
  var active = null;

  /** A dialog.close() queued at the end of a fade-out: { modal, timer }. */
  var pendingClose = null;

  /**
   * @param {Element} modal
   * @param {string} name
   * @returns {HTMLElement|null}
   */
  function slot(modal, name) {
    return modal.querySelector('[data-ugc-modal-' + name + ']');
  }

  /**
   * The real (non-clone) cards of one carousel, in author order. Read fresh
   * every open: the theme editor re-renders the section on every edit.
   *
   * @param {Element} root
   * @returns {HTMLElement[]}
   */
  function entriesOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll(REAL_CARD));
  }

  /**
   * @param {number} n
   * @returns {string}
   */
  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /**
   * Sets an element's text, hiding it when empty - an empty <p> still
   * occupies its margins and leaves a gap.
   *
   * @param {HTMLElement|null} el
   * @param {string} value
   */
  function fill(el, value) {
    if (!el) return;
    el.textContent = value || '';
    el.hidden = !value;
  }

  /**
   * Writes one entry into the shared template.
   *
   * @param {Element} modal
   * @param {HTMLElement} card
   * @param {number} index
   * @param {number} total
   */
  function populate(modal, card, index, total) {
    var data = card.dataset;

    /* two <img>s, one src: the visible photo and the full-bleed wash the info
       panel's backdrop-filter frosts - same URL, so no extra request */
    var image = /** @type {HTMLImageElement|null} */ (slot(modal, 'image'));
    var wash = /** @type {HTMLImageElement|null} */ (slot(modal, 'wash'));

    /* An entry with no photo has to actively clear both <img>s: two live
       entries point at files no longer in the store, and leaving the previous
       src made prev/next look like only the caption had changed. */
    if (image) {
      if (data.ugcPhoto) {
        image.hidden = false;
        image.src = data.ugcPhoto;
        image.alt = data.ugcPhotoAlt || '';
      } else {
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      }
    }

    if (wash) {
      if (data.ugcPhoto) {
        wash.hidden = false;
        wash.src = data.ugcPhoto;
      } else {
        /* no photo: the panel's flat surface shows through the glass */
        wash.hidden = true;
        wash.removeAttribute('src');
      }
    }

    var handle = slot(modal, 'handle');
    if (handle) {
      /* The section-level account name is the default, cached on first use so
         it survives navigating from an overriding entry back to a plain one. */
      if (handle.dataset.ugcDefault === undefined) {
        handle.dataset.ugcDefault = handle.textContent.trim();
      }
      handle.textContent = data.ugcHandle || handle.dataset.ugcDefault;
    }

    fill(slot(modal, 'likes'), data.ugcLikes);
    fill(slot(modal, 'comments'), data.ugcComments);
    fill(slot(modal, 'quote'), data.ugcQuote);
    fill(slot(modal, 'time'), data.ugcTime);

    var name = slot(modal, 'name');
    /* not a literal em dash - CLAUDE.md flags them as a corruption risk for
       anything in this repo edited from PowerShell */
    if (name) fill(name, data.ugcName ? '— ' + data.ugcName : '');

    var counter = slot(modal, 'counter');
    if (counter) counter.textContent = pad(index + 1) + ' / ' + pad(total);

    populateProduct(modal, data);
  }

  /**
   * "Shop the look" is optional. With no linked product the section is hidden
   * and its <a> emptied, so a stale product is never one tap away behind it.
   *
   * @param {Element} modal
   * @param {DOMStringMap} data
   */
  function populateProduct(modal, data) {
    var shop = slot(modal, 'shop');
    if (!shop) return;

    var link = /** @type {HTMLAnchorElement|null} */ (slot(modal, 'product-link'));
    var thumb = /** @type {HTMLImageElement|null} */ (slot(modal, 'product-image'));

    if (!data.ugcProductUrl) {
      shop.hidden = true;
      if (link) link.removeAttribute('href');
      return;
    }

    shop.hidden = false;
    if (link) link.href = data.ugcProductUrl;
    fill(slot(modal, 'product-title'), data.ugcProductTitle);
    fill(slot(modal, 'product-price'), data.ugcProductPrice);

    if (thumb) {
      thumb.hidden = !data.ugcProductImage;
      if (data.ugcProductImage) {
        thumb.src = data.ugcProductImage;
        thumb.alt = data.ugcProductTitle || '';
      }
    }
  }

  /**
   * showModal() does not stop the page behind scrolling. Padding the body by
   * the scrollbar's width keeps the header and footer from jumping sideways.
   */
  function lockScroll() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.dataset.ugcScrollLock = 'true';
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
  }

  function unlockScroll() {
    if (!document.body.dataset.ugcScrollLock) return;
    delete document.body.dataset.ugcScrollLock;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /**
   * @param {Element} root
   * @param {number} index
   * @param {Element=} returnFocusTo
   */
  function open(root, index, returnFocusTo) {
    var modal = /** @type {HTMLDialogElement|null} */ (root.querySelector('[data-ugc-modal]'));
    if (!modal) return;

    var entries = entriesOf(root);
    if (!entries.length) return;

    var i = ((index % entries.length) + entries.length) % entries.length;
    populate(modal, entries[i], i, entries.length);

    if (active && active.modal === modal) {
      active.index = i;
      return;
    }

    if (active) closeNow();

    /* A close() started less than CLOSE_ANIMATION_MS ago still has a timer
       queued to call dialog.close(); reopening inside that window would let it
       fire against the freshly reopened dialog. Cancel it, and do not call
       showModal() on a dialog it has not closed yet - that throws
       InvalidStateError. */
    cancelPendingClose();

    active = {
      root: root,
      modal: modal,
      index: i,
      trigger: returnFocusTo || null,
      timer: null,
      /* Guards the backdrop-click close below: the modal opens on pointerup,
         one step ahead of the click synthesized from that same tap, which
         would otherwise close it in the gesture that opened it. */
      openedAt: performance.now(),
    };

    if (!modal.open) modal.showModal();
    lockScroll();
    /* next frame, not this one: the transition needs a painted closed state
       to animate away from, and showModal() only reveals at end of tick */
    requestAnimationFrame(function () {
      modal.classList.add('is-open');
    });
  }

  /**
   * @param {number} step
   */
  function move(step) {
    if (!active) return;
    var entries = entriesOf(active.root);
    if (!entries.length) return;
    var next = ((active.index + step) % entries.length + entries.length) % entries.length;
    active.index = next;
    populate(active.modal, entries[next], next, entries.length);
  }

  /**
   * Drops a queued "finish closing" timer without letting it fire.
   */
  function cancelPendingClose() {
    if (!pendingClose) return;
    clearTimeout(pendingClose.timer);
    pendingClose = null;
  }

  /** Tears the modal down immediately, skipping the fade. */
  function closeNow() {
    if (!active) return;
    var current = active;
    active = null;

    cancelPendingClose();
    clearTimeout(current.timer);
    current.modal.classList.remove('is-open');
    if (current.modal.open) current.modal.close();
    unlockScroll();

    if (current.trigger && document.contains(current.trigger)) {
      current.trigger.focus({ preventScroll: true });
    }
  }

  function close() {
    if (!active) return;
    var current = active;
    active = null;

    current.modal.classList.remove('is-open');
    cancelPendingClose();
    pendingClose = {
      modal: current.modal,
      timer: setTimeout(function () {
        pendingClose = null;
        if (current.modal.open) current.modal.close();
      }, CLOSE_ANIMATION_MS),
    };
    current.timer = pendingClose.timer;

    unlockScroll();

    if (current.trigger && document.contains(current.trigger)) {
      current.trigger.focus({ preventScroll: true });
    }
  }

  /**
   * @param {Element} trigger
   */
  function openFromTrigger(trigger) {
    var card = trigger.closest(CARD);
    var root = trigger.closest(ROOT);
    if (!card || !root) return;

    var index = Number(card.dataset.ugcIndex);
    if (!isFinite(index)) {
      /* the carousel stamps data-ugc-index while building the loop's second
         half; if it has not run, fall back to position among the real ones */
      index = entriesOf(root).indexOf(/** @type {HTMLElement} */ (card));
    }
    if (index < 0) return;

    open(root, index, trigger);
  }

  /* --------------------------------------------------- gesture handling */

  /**
   * @param {PointerEvent} event
   */
  function onPointerDown(event) {
    var trigger = event.target instanceof Element ? event.target.closest(TRIGGER) : null;
    if (!trigger) return;

    gestures.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      t: performance.now(),
      trigger: trigger,
    });
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerMove(event) {
    var gesture = gestures.get(event.pointerId);
    if (!gesture) return;

    if (
      Math.abs(event.clientX - gesture.x) > THRESHOLD ||
      Math.abs(event.clientY - gesture.y) > THRESHOLD
    ) {
      /* past the threshold this stays a drag even if the finger returns to
         where it started, so the release cannot open anything */
      gestures.delete(event.pointerId);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerUp(event) {
    var gesture = gestures.get(event.pointerId);
    if (!gesture) return;
    gestures.delete(event.pointerId);

    if (performance.now() - gesture.t > TAP_MAX_MS) return;
    openFromTrigger(gesture.trigger);
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerCancel(event) {
    gestures.delete(event.pointerId);
  }

  /**
   * @param {KeyboardEvent} event
   */
  function onKeydown(event) {
    if (active) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var trigger = event.target instanceof Element ? event.target.closest(TRIGGER) : null;
    if (!trigger || trigger.getAttribute('tabindex') === '-1') return;

    event.preventDefault();
    openFromTrigger(trigger);
  }

  /**
   * @param {MouseEvent} event
   */
  function onClick(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-ugc-modal-close]')) {
      close();
      return;
    }
    if (event.target.closest('[data-ugc-modal-prev]')) {
      move(-1);
      return;
    }
    if (event.target.closest('[data-ugc-modal-next]')) {
      move(1);
      return;
    }

    /* a click on the dialog itself is a click on the dimmed page */
    if (!active || performance.now() - active.openedAt < OPEN_CLICK_GRACE_MS) return;
    if (event.target.matches('[data-ugc-modal]')) close();
  }

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerCancel, true);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('click', onClick);

  /* Escape fires the dialog's own cancel, which would close instantly and
     skip the fade - cancel that and route through close() instead. */
  document.addEventListener(
    'cancel',
    function (event) {
      if (!active || event.target !== active.modal) return;
      event.preventDefault();
      close();
    },
    true
  );

  /* a theme-editor re-render replaces the dialog node; drop the reference
     rather than leave the scroll lock applied to a modal that is gone */
  document.addEventListener('shopify:section:unload', function () {
    if (active && !document.contains(active.modal)) {
      active = null;
      unlockScroll();
    }
  });
})();
